// PointerController (IMPLEMENTATION §2.7 "Pointer FSM", §2.8): the DOM glue around the PURE gesture FSM.
//   - one active pointer id; pointerdown on the persistent full-screen #input root, which then takes pointer
//     capture (setPointerCapture) — and window capture-phase move/up/cancel listeners filtered by that
//     pointerId, so a failed capture retarget (iOS) still delivers the gesture (M0 decision 3);
//   - timers: the FSM's next deadline (hold 250 ms, long-press 400 ms) as one setTimeout;
//   - aborts: pointercancel, a lostpointercapture we did not cause (drags), window blur, a hidden page;
//   - `adopt(e)`: the §2.8 shop handoff (a DOM card gives its pointer to #input mid-gesture).
// It knows nothing about the game: the router classifies a pointerdown (`classify`) and receives the
// gesture outputs (`onGesture`) with the pointer type and whether #input still held capture.
import type { Cell } from '../layout/computeLayout.ts';
import {
  activePointer,
  type CancelReason,
  type DownKind,
  GESTURE_IDLE,
  type GestureCfg,
  type GestureEvent,
  type GestureOut,
  type GestureState,
  type GestureStateKind,
  gestureDeadline,
  gestureKind,
  gestureStep,
} from './gestureFsm.ts';

export interface GestureMeta {
  pointerType: string;
  /** #input held pointer capture for the active pointer (false = the window fallback carried it). */
  captured: boolean;
}

export interface PointerControllerDeps {
  root: HTMLElement;
  win: Window;
  cfg: GestureCfg;
  now(): number;
  /** The §2.7 IDLE --down--> decision for a pointer at client (x, y). */
  classify(x: number, y: number, mouse: boolean): DownKind;
  /** The board cell under a client point (hold: leaving the deposit; paint strokes). */
  cellAt(x: number, y: number): Cell | null;
  onGesture(out: readonly GestureOut[], meta: GestureMeta): void;
}

export interface PointerController {
  /** §2.8 step 3: take over a pointer that started on a DOM card (true = adopted, the FSM is in DRAG). */
  adopt(e: PointerEvent): boolean;
  /** Aborts the active gesture (mode switch, Esc). */
  cancel(reason: CancelReason): void;
  dragging(): boolean;
  kind(): GestureStateKind;
  destroy(): void;
}

export function createPointerController(d: PointerControllerDeps): PointerController {
  const { root, win, cfg } = d;
  let st: GestureState = GESTURE_IDLE;
  let pointerType = 'mouse';
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** We are releasing capture ourselves: the resulting lostpointercapture is not an abort. */
  let releasing = false;

  const holdsCapture = (id: number): boolean => {
    try {
      return root.hasPointerCapture(id);
    } catch {
      return false;
    }
  };

  const capture = (id: number): void => {
    try {
      root.setPointerCapture(id);
    } catch {
      // window listeners still deliver move / up / cancel for this pointerId
    }
  };

  const release = (id: number): void => {
    if (!holdsCapture(id)) return;
    releasing = true;
    try {
      root.releasePointerCapture(id);
    } catch {
      // pointer already gone
    }
    releasing = false;
  };

  const schedule = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const due = gestureDeadline(st, cfg);
    if (due === null) return;
    timer = setTimeout(
      () => {
        timer = null;
        feed({ k: 'time', t: d.now() });
      },
      Math.max(0, due - d.now()),
    );
  };

  const feed = (e: GestureEvent): void => {
    const before = activePointer(st);
    const meta: GestureMeta = {
      pointerType,
      captured: before !== null && holdsCapture(before),
    };
    const r = gestureStep(st, e, cfg);
    st = r.s;
    const after = activePointer(st);
    if (before !== null && after !== before) release(before);
    schedule();
    if (r.out.length > 0) d.onGesture(r.out, meta);
  };

  const onDown = (e: PointerEvent): void => {
    // Mouse: only the main button taps / drags (right / middle click is not a tap; contextmenu is blocked).
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const mouse = e.pointerType === 'mouse';
    const idle = activePointer(st) === null;
    // a 2nd pointer only matters to the palm guard: no hitTest for it
    const kind: DownKind = idle
      ? d.classify(e.clientX, e.clientY, mouse)
      : { k: 'press', obj: 'none', draggable: false, longPress: false };
    if (idle) pointerType = e.pointerType;
    feed({ k: 'down', id: e.pointerId, x: e.clientX, y: e.clientY, t: d.now(), mouse, kind });
    if (idle && activePointer(st) === e.pointerId) capture(e.pointerId);
  };

  const mine = (e: PointerEvent): boolean => activePointer(st) === e.pointerId;

  const onMove = (e: PointerEvent): void => {
    if (!mine(e)) return;
    feed({
      k: 'move',
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      t: d.now(),
      cell: d.cellAt(e.clientX, e.clientY),
    });
  };

  const onUp = (e: PointerEvent): void => {
    if (!mine(e)) return;
    feed({ k: 'up', id: e.pointerId, x: e.clientX, y: e.clientY, t: d.now() });
  };

  const onCancel = (e: PointerEvent): void => {
    if (!mine(e)) return;
    feed({ k: 'cancel', id: e.pointerId, t: d.now(), reason: 'pointercancel' });
  };

  const onLostCapture = (e: PointerEvent): void => {
    if (releasing || !mine(e)) return;
    feed({ k: 'cancel', id: e.pointerId, t: d.now(), reason: 'lostpointercapture' });
  };

  const cancelAll = (reason: CancelReason): void => {
    if (activePointer(st) === null) return;
    feed({ k: 'cancel', id: null, t: d.now(), reason });
  };
  const onBlur = (): void => cancelAll('blur');
  const onVisibility = (): void => {
    if (win.document.visibilityState === 'hidden') cancelAll('hidden');
  };

  const cap = { capture: true } as const;
  root.addEventListener('pointerdown', onDown);
  root.addEventListener('lostpointercapture', onLostCapture);
  win.addEventListener('pointermove', onMove, cap);
  win.addEventListener('pointerup', onUp, cap);
  win.addEventListener('pointercancel', onCancel, cap);
  win.addEventListener('blur', onBlur);
  win.document.addEventListener('visibilitychange', onVisibility);

  return {
    adopt(e) {
      if (activePointer(st) !== null) return false;
      pointerType = e.pointerType;
      feed({
        k: 'adopt',
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        t: d.now(),
        mouse: e.pointerType === 'mouse',
      });
      if (activePointer(st) !== e.pointerId) return false;
      capture(e.pointerId);
      return true;
    },
    cancel: cancelAll,
    dragging: () => gestureKind(st) === 'drag',
    kind: () => gestureKind(st),
    destroy() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      root.removeEventListener('pointerdown', onDown);
      root.removeEventListener('lostpointercapture', onLostCapture);
      win.removeEventListener('pointermove', onMove, cap);
      win.removeEventListener('pointerup', onUp, cap);
      win.removeEventListener('pointercancel', onCancel, cap);
      win.removeEventListener('blur', onBlur);
      win.document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
